import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private readonly STORAGE_KEY = 'kig-theme';
  private readonly darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

  private themeSubject = new BehaviorSubject<Theme>(this.resolveInitialTheme());
  public currentTheme$ = this.themeSubject.asObservable();

  constructor() {
    // Clear any stale persisted preference since the manual toggle
    // is not currently rendered (res-header has no default content slot).
    localStorage.removeItem(this.STORAGE_KEY);

    this.applyTheme(this.themeSubject.value);

    this.mediaListener = (e: MediaQueryListEvent) => {
      const theme: Theme = e.matches ? 'dark' : 'light';
      this.themeSubject.next(theme);
      this.applyTheme(theme);
    };
    this.darkMediaQuery.addEventListener('change', this.mediaListener);
  }

  ngOnDestroy(): void {
    if (this.mediaListener) {
      this.darkMediaQuery.removeEventListener('change', this.mediaListener);
    }
  }

  getTheme(): Theme {
    return this.themeSubject.value;
  }

  setTheme(theme: Theme): void {
    localStorage.setItem(this.STORAGE_KEY, theme);
    this.themeSubject.next(theme);
    this.applyTheme(theme);
  }

  toggleTheme(): void {
    const next: Theme = this.themeSubject.value === 'dark' ? 'light' : 'dark';
    this.setTheme(next);
  }

  private resolveInitialTheme(): Theme {
    return this.darkMediaQuery.matches ? 'dark' : 'light';
  }

  private applyTheme(theme: Theme): void {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
    html.setAttribute('data-bs-theme', theme);
  }
}
